import { ArcDataTab, DataTab } from '@/config/interfaces'
import { apiRoutes } from '@/helpers/ApiRoutes'
import VueRouter from 'vue-router'

export default {
  round: function (num: number, places = 1): number {
    const x = Math.pow(10, places)
    return Math.round(num * x) / x
  },
  createGraphData: function (dataTab: DataTab): Array<ArcDataTab> {
    const emptyColor = '#D6D6D6'
    const { vals, unlimited } = dataTab
    const currentValueSum = dataTab.vals[dataTab.vals.length - 1].val
    let maxVal = dataTab.maxVal
    let totalDataAmount = 0 // Contains (in percents) available data amount

    // If the sum of all values is greater than initial max value, for converting to percents,
    // currentValueSum becomes new maxValue
    if (maxVal < currentValueSum) {
      maxVal = currentValueSum
    }

    // Mapping to transform data to percentages for easier manipulation
    const numData: Array<ArcDataTab> = vals.map((item, idx) => {
      let dataPercentage: number

      if (idx > 0 && vals.length > 1) {
        dataPercentage = this.round(((item.val - vals[idx - 1].val) / maxVal) * 100, 2)
      } else {
        dataPercentage = this.round(((item.val / maxVal) * 100), 2)
      }

      totalDataAmount += dataPercentage

      return {
        color: item.color,
        val: dataPercentage
      }
    }).filter(item => item.val > 0) // Remove empty values from dataset (no need to render them)

    // If data has been used, show a gray part as an 'empty' part
    if (totalDataAmount < 100 && !unlimited) {
      numData.push({ val: this.round(100 - totalDataAmount, 2), color: emptyColor })
    }

    return numData
  },
  getPostParamsByRefs: function (refs: Array<string>): URLSearchParams {
    const postParams = new URLSearchParams()
    for (let i = 0; i < refs.length; i++) {
      const key = refs[i]
      const doc = document.getElementById(key) as HTMLInputElement
      postParams.append(key, doc.value)
    }

    return postParams
  },
  degreesToRadians: function (num: number): number {
    return num * Math.PI / 180
  },
  calculateDistance: function (lat1: number, lon1: number, lat2: number, lon2: number): number {
    const RADIUS = 6371

    const deltaLatitude = this.degreesToRadians(lat2 - lat1)
    const deltaLongitude = this.degreesToRadians(lon2 - lon1)

    const circumference = Math.sin(deltaLatitude / 2) *
        Math.sin(deltaLatitude / 2) +
        Math.cos(this.degreesToRadians(lat1)) *
        Math.cos(this.degreesToRadians(lat2)) *
        Math.sin(deltaLongitude / 2) *
        Math.sin(deltaLongitude / 2)

    const center = 2 *
        Math.atan2(Math.sqrt(circumference), Math.sqrt(1 - circumference))
    const distanceInKM = RADIUS * center

    return this.round(distanceInKM, 2)
  },
  finalizeLogout: function (): void {
    const oAuthLoginUrl = process.env.VUE_APP_AXIOS_BASE_URL + apiRoutes.pwaLogin
    localStorage.clear()
    sessionStorage.clear()
    window.open(oAuthLoginUrl, '_self')
  },
  isOldBrowser: function (): boolean {
    const { userAgent } = window.navigator
    // IE 10 or below
    const msie = userAgent.indexOf('MSIE ')
    // IE 11
    const trident = userAgent.indexOf('Trident/')
    // Vuetify doesn't support Safari versions below 9
    if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) {
      const version = 'Version/'
      // Safari user agent looks like Version/8.0.8 Safari/600.8.9
      // We need to get first 2 numbers after Version/
      const safariVersion = parseInt(userAgent.slice(userAgent.indexOf(version) + version.length).slice(0, 2))
      return safariVersion < 9
    }
    return msie > 0 || trident > 0
  },
  urlBase64ToUint8Array: function (base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; i++) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  },
  isSafariBrowser: function (): boolean {
    const { userAgent } = window.navigator
    if (userAgent.indexOf('Safari') !== -1) {
      return true
    }
    return false
  },
  reloadPage: function (router: VueRouter): void {
    if (this.isSafariBrowser()) {
      window.location.reload()
    } else {
      router.go(0)
    }
  }
}
